<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import GovernmentSidebar from "@/components/government/GovernmentSidebar.vue";
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <GovernmentSidebar />
    <div id="main-content" class="flex-1 flex flex-col">
      <!-- Header -->
      <header id="header" class="bg-white border-b border-light-gray p-5">
        <div class="flex justify-between items-center">
          <div>
            <h2 class="text-2xl font-medium text-dark-navy">
              {{ $t('government.certificates.title') }}
            </h2>
            <p class="text-gray-600">{{ $t('government.certificates.subtitle') }}</p>
          </div>
          <div class="flex items-center space-x-4">
            <button
              class="p-2 rounded-md border border-light-gray hover:bg-gray-50"
            >
              <i class="fa-solid fa-bell text-gray-600"></i>
            </button>
            <div class="flex items-center space-x-3">
              <img
                src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                alt="Admin"
                class="w-8 h-8 rounded-full"
              />
              <span class="text-sm font-medium">{{ $t('government.dashboard_page.admin_user') }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- Certificates Content -->
      <main id="certificates-main" class="flex-1 p-5 overflow-y-auto">
        <!-- Action Buttons -->
        <div id="action-buttons" class="flex flex-wrap gap-4 mb-6">
          <button
            class="px-6 py-3 bg-forest-green text-white rounded-md font-medium hover:bg-green-700 flex items-center"
          >
            <i class="fa-solid fa-plus mr-2"></i>
            {{ $t('government.certificates.action_buttons.issue_cr') }}
          </button>
          <button
            class="px-6 py-3 bg-electric-blue text-dark-navy rounded-md font-medium hover:bg-cyan-400 flex items-center"
          >
            <i class="fa-solid fa-file-alt mr-2"></i>
            {{ $t('government.certificates.action_buttons.issue_loa') }}
          </button>
          <button
            class="px-6 py-3 bg-dark-navy text-white rounded-md font-medium hover:bg-blue-900 flex items-center"
          >
            <i class="fa-solid fa-ban mr-2"></i>
            {{ $t('government.certificates.action_buttons.issue_cci') }}
          </button>
        </div>

        <!-- Filters and Search -->
        <div
          id="filters-section"
          class="bg-white p-5 rounded-md border border-light-gray mb-6"
        >
          <div class="flex flex-wrap gap-4 items-center">
            <div class="flex-1 min-w-64">
              <input
                type="text"
                :placeholder="$t('government.certificates.filters.search_placeholder')"
                class="w-full px-4 py-2 border border-light-gray rounded-md"
              />
            </div>
            <select class="px-4 py-2 border border-light-gray rounded-md">
              <option>{{ $t('government.certificates.filters.all_certificate_types') }}</option>
              <option>{{ $t('government.certificates.filters.credit_registration') }}</option>
              <option>{{ $t('government.certificates.filters.letter_authorization') }}</option>
              <option>{{ $t('government.certificates.filters.credit_cancellation') }}</option>
            </select>
            <select class="px-4 py-2 border border-light-gray rounded-md">
              <option>{{ $t('government.certificates.filters.all_status') }}</option>
              <option>{{ $t('government.certificates.filters.draft') }}</option>
              <option>{{ $t('government.certificates.filters.pending_approval') }}</option>
              <option>{{ $t('government.certificates.filters.issued') }}</option>
              <option>{{ $t('government.certificates.filters.cancelled') }}</option>
            </select>
            <button
              class="px-4 py-2 bg-forest-green text-white rounded-md hover:bg-green-700"
            >
              <i class="fa-solid fa-filter mr-2"></i>{{ $t('government.certificates.filters.filter') }}
            </button>
          </div>
        </div>

        <!-- Statistics Cards -->
        <div id="cert-stats" class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.certificates.stats.total_issued') }}</p>
                <p class="text-2xl font-medium text-dark-navy">1,247</p>
              </div>
              <i class="fa-solid fa-certificate text-forest-green text-xl"></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.certificates.stats.cr_documents') }}</p>
                <p class="text-2xl font-medium text-forest-green">856</p>
              </div>
              <i class="fa-solid fa-file-check text-forest-green text-xl"></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.certificates.stats.loa_documents') }}</p>
                <p class="text-2xl font-medium text-electric-blue">298</p>
              </div>
              <i
                class="fa-solid fa-file-signature text-electric-blue text-xl"
              ></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.certificates.stats.cci_documents') }}</p>
                <p class="text-2xl font-medium text-error-red">93</p>
              </div>
              <i class="fa-solid fa-file-times text-error-red text-xl"></i>
            </div>
          </div>
        </div>

        <!-- Certificates Table -->
        <div
          id="certificates-table"
          class="bg-white rounded-md border border-light-gray"
        >
          <div class="p-5 border-b border-light-gray">
            <h3 class="text-lg font-medium text-dark-navy">
              {{ $t('government.certificates.table.title') }}
            </h3>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-sm font-medium text-gray-600"
                  >
                    {{ $t('government.certificates.table.columns.document_id') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-sm font-medium text-gray-600"
                  >
                    {{ $t('government.certificates.table.columns.type') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-sm font-medium text-gray-600"
                  >
                    {{ $t('government.certificates.table.columns.project_name') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-sm font-medium text-gray-600"
                  >
                    {{ $t('government.certificates.table.columns.organization') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-sm font-medium text-gray-600"
                  >
                    {{ $t('government.certificates.table.columns.issue_date') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-sm font-medium text-gray-600"
                  >
                    {{ $t('government.certificates.table.columns.status') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-sm font-medium text-gray-600"
                  >
                    {{ $t('government.certificates.table.columns.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr>
                  <td class="px-6 py-4 text-sm font-medium text-dark-navy">
                    CR-2024-078
                  </td>
                  <td class="px-6 py-4 text-sm">
                    <span
                      class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                      >{{ $t('government.certificates.table.document_types.cr') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 text-sm">Solar Farm Matabeleland</td>
                  <td class="px-6 py-4 text-sm">Green Energy ZW</td>
                  <td class="px-6 py-4 text-sm">2024-01-15</td>
                  <td class="px-6 py-4 text-sm">
                    <span
                      class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                      >{{ $t('government.certificates.filters.issued') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 text-sm">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-cyan-600 mr-3">
                      <i class="fa-solid fa-download"></i>
                    </button>
                    <button class="text-gray-600 hover:text-gray-800">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                  </td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm font-medium text-dark-navy">
                    LOA-2024-089
                  </td>
                  <td class="px-6 py-4 text-sm">
                    <span
                      class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                      >{{ $t('government.certificates.table.document_types.loa') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 text-sm">
                    Forest Conservation Manicaland
                  </td>
                  <td class="px-6 py-4 text-sm">EcoForest Ltd</td>
                  <td class="px-6 py-4 text-sm">2024-01-14</td>
                  <td class="px-6 py-4 text-sm">
                    <span
                      class="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs"
                      >{{ $t('government.application_queue.filters.pending_review') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 text-sm">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-cyan-600 mr-3">
                      <i class="fa-solid fa-check"></i>
                    </button>
                    <button class="text-gray-600 hover:text-gray-800">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                  </td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm font-medium text-dark-navy">
                    CCI-2024-012
                  </td>
                  <td class="px-6 py-4 text-sm">
                    <span
                      class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                      >{{ $t('government.certificates.table.document_types.cci') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 text-sm">Wind Farm Mashonaland</td>
                  <td class="px-6 py-4 text-sm">WindPower Corp</td>
                  <td class="px-6 py-4 text-sm">2024-01-13</td>
                  <td class="px-6 py-4 text-sm">
                    <span
                      class="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                      >{{ $t('government.certificates.filters.cancelled') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 text-sm">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-cyan-600 mr-3">
                      <i class="fa-solid fa-download"></i>
                    </button>
                    <button class="text-gray-600 hover:text-gray-800">
                      <i class="fa-solid fa-history"></i>
                    </button>
                  </td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm font-medium text-dark-navy">
                    CR-2024-077
                  </td>
                  <td class="px-6 py-4 text-sm">
                    <span
                      class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                      >{{ $t('government.certificates.table.document_types.cr') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 text-sm">Biogas Plant Harare</td>
                  <td class="px-6 py-4 text-sm">BioEnergy Solutions</td>
                  <td class="px-6 py-4 text-sm">2024-01-12</td>
                  <td class="px-6 py-4 text-sm">
                    <span
                      class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                      >{{ $t('government.certificates.filters.draft') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 text-sm">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-cyan-600 mr-3">
                      <i class="fa-solid fa-check"></i>
                    </button>
                    <button class="text-gray-600 hover:text-gray-800">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                  </td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm font-medium text-dark-navy">
                    LOA-2024-088
                  </td>
                  <td class="px-6 py-4 text-sm">
                    <span
                      class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                      >{{ $t('government.certificates.table.document_types.loa') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 text-sm">Hydro Power Zambezi</td>
                  <td class="px-6 py-4 text-sm">Zambezi Power Co</td>
                  <td class="px-6 py-4 text-sm">2024-01-11</td>
                  <td class="px-6 py-4 text-sm">
                    <span
                      class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                      >{{ $t('government.certificates.filters.issued') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 text-sm">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-cyan-600 mr-3">
                      <i class="fa-solid fa-download"></i>
                    </button>
                    <button class="text-gray-600 hover:text-gray-800">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            class="p-5 border-t border-light-gray flex justify-between items-center"
          >
            <span class="text-sm text-gray-600"
              >{{ $t('government.certificates.pagination.showing') }} 1-5 {{ $t('government.certificates.pagination.of') }} 1,247 {{ $t('government.certificates.pagination.certificates') }}</span
            >
            <div class="flex space-x-2">
              <button
                class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
              >
                {{ $t('government.certificates.pagination.previous') }}
              </button>
              <button
                class="px-3 py-1 bg-forest-green text-white rounded-md text-sm"
              >
                1
              </button>
              <button
                class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
              >
                2
              </button>
              <button
                class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
              >
                3
              </button>
              <button
                class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
              >
                {{ $t('government.certificates.pagination.next') }}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>
